import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { usePostHog } from '@posthog/react';
import { Provider } from 'inversify-react';
import React, { FC, ReactNode, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';

import useCaseAppStartup from 'application/modules/bookingWizard/useCases/hooks/useCaseAppStartup';
import InfrastructureProvider from 'infrastructure/contexts/InfrastructureProvider';
import { MetricsProvider } from 'infrastructure/services/metrics/MetricsProvider';

import container from './container';
import { TextCopyContextProvider } from './providers/TextCopyContextProvider';
import Router from './routes/Router';
import { ColorModeProvider } from './theme/ColorModeProvider';
import { isAustralia, isUAE } from './modules/common/helpers';

const sentryEnv = import.meta.env.VITE_SENTRY_ENV || 'development';
const isProduction = sentryEnv?.search(/production/) === 0;
const metricsAuthToken = import.meta.env.VITE_METRICS_AUTH_TOKEN;
const backendApiBaseUrl = import.meta.env.VITE_BACKEND_API_BASE_URL || '';

function PostHogEnvironmentSetup({
  environment,
  region,
}: {
  environment: string;
  region: string;
}) {
  const posthog = usePostHog();
  useEffect(() => {
    posthog?.register({ environment, region });
  }, [posthog, environment, region]);
  return null;
}

interface IAppWrapper {
  children?: ReactNode;
}

const AppWrapper: FC<IAppWrapper> = ({ children }) => {
  const { result, error } = useCaseAppStartup();
  if (!result && error) {
    console.error('Error while initializing app', error);
  }
  return <>{children}</>;
};
export default function App() {
  const getRegion = () => {
    if (isAustralia()) {
      return 'au';
    } else if (isUAE()) {
      return 'ae';
    } else {
      return 'us';
    }
  }
  return (
    <HelmetProvider>
      {import.meta.env.VITE_POSTHOG_API_KEY && (
        <PostHogEnvironmentSetup environment={sentryEnv} region={getRegion()} />
      )}
      <Provider container={container}>
        <MetricsProvider
          baseUrl={backendApiBaseUrl}
          platform="web"
          environment={sentryEnv}
          region={getRegion()}
          authToken={metricsAuthToken}
          enabled={isProduction}
        >
          <InfrastructureProvider>
            <AppWrapper>
              <ColorModeProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TextCopyContextProvider>
                    <Router />
                  </TextCopyContextProvider>
                </LocalizationProvider>
              </ColorModeProvider>
            </AppWrapper>
          </InfrastructureProvider>
        </MetricsProvider>
      </Provider>
    </HelmetProvider>
  );
}
